import {
  WS_CLOCK_SYNC_INTERVAL_MS,
  WS_PROTOCOL_VERSION,
  zServerMessage,
  type SessionHelloPayload,
  type WsErrorPayload,
} from '@badger-sentinel/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import { openSocket } from '../api/transport.js';

import { ClockEstimator } from './clock.js';

/**
 * The game socket (§8.4, §8.5).
 *
 * M0 exercises the transport only: connect, greet, clock-sync, reconnect. The
 * per-seat subscription, snapshot/patch handling, and `resume` replay land with M2
 * and hang off the same message switch.
 *
 * Reconnection is not optional polish. A dropped connection at minute 4 of a
 * 5-minute orders window must recover on its own — the seat is held for 120 s
 * server-side precisely so that it can (§3.2).
 */
export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface GameSocketState {
  status: SocketStatus;
  hello: SessionHelloPayload | null;
  /** Add to `Date.now()` to get authoritative server time (§8.2). */
  clockOffsetMs: number;
  roundTripMs: number | null;
  clockSamples: number;
  lastError: string | null;
  /** Server-sent errors, newest first. Kept short — this is a diagnostic surface. */
  serverErrors: WsErrorPayload[];
  reconnectAttempts: number;
}

const INITIAL: GameSocketState = {
  status: 'connecting',
  hello: null,
  clockOffsetMs: 0,
  roundTripMs: null,
  clockSamples: 0,
  lastError: null,
  serverErrors: [],
  reconnectAttempts: 0,
};

/** Exponential backoff with a 30 s ceiling, so a server restart is ridden out. */
function backoffMs(attempt: number): number {
  return Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
}

export function useGameSocket(wsUrl: string | null): GameSocketState & { sendPing: () => void } {
  const [state, setState] = useState<GameSocketState>(INITIAL);

  const socketRef = useRef<WebSocket | null>(null);
  const clockRef = useRef(new ClockEstimator());
  const attemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUsRef = useRef(false);

  const sendPing = useCallback(() => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(
      JSON.stringify({
        v: WS_PROTOCOL_VERSION,
        seq: 0,
        type: 'ping',
        payload: { clientSentAtMs: Date.now() },
      }),
    );
  }, []);

  useEffect(() => {
    if (wsUrl === null) return;

    closedByUsRef.current = false;
    let syncTimer: ReturnType<typeof setInterval> | null = null;

    const connect = (): void => {
      setState((prev) => ({
        ...prev,
        status: attemptsRef.current === 0 ? 'connecting' : 'reconnecting',
        reconnectAttempts: attemptsRef.current,
      }));

      const socket = openSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        attemptsRef.current = 0;
        setState((prev) => ({ ...prev, status: 'open', lastError: null, reconnectAttempts: 0 }));
        sendPing();
        syncTimer = setInterval(sendPing, WS_CLOCK_SYNC_INTERVAL_MS);
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        const clientReceivedAtMs = Date.now();

        let parsed: unknown;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          setState((prev) => ({ ...prev, lastError: 'Server sent a non-JSON frame.' }));
          return;
        }

        const message = zServerMessage.safeParse(parsed);
        if (!message.success) {
          // A frame the shared schema rejects means client and server disagree about
          // the wire format — almost always a stale tab after a deploy.
          setState((prev) => ({
            ...prev,
            lastError: 'Server sent a frame this client does not understand. Reload the page.',
          }));
          return;
        }

        switch (message.data.type) {
          case 'session.hello': {
            const hello = message.data.payload;
            setState((prev) => ({ ...prev, hello }));
            return;
          }
          case 'pong': {
            const sample = clockRef.current.observe({
              clientSentAtMs: message.data.payload.clientSentAtMs,
              serverTimeMs: message.data.payload.serverTimeMs,
              clientReceivedAtMs,
            });
            setState((prev) => ({
              ...prev,
              clockOffsetMs: clockRef.current.offsetMs,
              roundTripMs: sample.roundTripMs,
              clockSamples: clockRef.current.sampleCount,
            }));
            return;
          }
          case 'error': {
            const payload = message.data.payload;
            setState((prev) => ({
              ...prev,
              serverErrors: [payload, ...prev.serverErrors].slice(0, 10),
            }));
            return;
          }
        }
      };

      socket.onerror = () => {
        setState((prev) => ({ ...prev, lastError: 'WebSocket error.' }));
      };

      socket.onclose = (event: CloseEvent) => {
        if (syncTimer !== null) {
          clearInterval(syncTimer);
          syncTimer = null;
        }
        if (closedByUsRef.current) {
          setState((prev) => ({ ...prev, status: 'closed' }));
          return;
        }

        const delay = backoffMs(attemptsRef.current);
        attemptsRef.current += 1;
        setState((prev) => ({
          ...prev,
          status: 'reconnecting',
          reconnectAttempts: attemptsRef.current,
          lastError:
            event.code === 4001
              ? 'Server is restarting for a deploy; reconnecting.'
              : (prev.lastError ?? `Connection closed (${event.code}).`),
        }));
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimerRef.current !== null) clearTimeout(reconnectTimerRef.current);
      if (syncTimer !== null) clearInterval(syncTimer);
      socketRef.current?.close(1000, 'component unmounted');
      socketRef.current = null;
      clockRef.current.reset();
    };
  }, [wsUrl, sendPing]);

  return { ...state, sendPing };
}
